import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import colors from '../theme/colors';
import { apiService } from '../services/api';
import { SocialNetworkIcon, SOCIAL_ICON_LABELS, SocialIconKey } from './social/SocialNetworkIcon';

const FooterContainer = styled.footer`
  background-color: ${colors.mainBlack};
  color: ${colors.mainWhite};
  padding: 1.75rem 0 1rem 0;
  margin-top: auto;
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const FooterTop = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 2rem;
  margin-bottom: 1.25rem;
  padding-bottom: 1.25rem;
  align-items: start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    text-align: center;
  }
`;

const FooterSection = styled.div<{ $align?: 'start' | 'center' | 'end' }>`
  display: flex;
  flex-direction: column;
  text-align: ${({ $align }) => ($align === 'center' ? 'center' : $align === 'end' ? 'right' : 'left')};
  align-items: ${({ $align }) =>
    $align === 'center' ? 'center' : $align === 'end' ? 'flex-end' : 'flex-start'};

  @media (max-width: 768px) {
    text-align: center;
    align-items: center;
  }
`;

const FooterTitle = styled.h3`
  margin: 0 0 0.75rem 0;
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: ${colors.mainWhite};
  text-transform: uppercase;
`;

const FooterText = styled.p`
  margin: 0;
  opacity: 0.8;
  color: ${colors.goldGrey};
  font-size: 0.9rem;
  line-height: 1.6;
`;

const ContactLine = styled.a`
  color: ${colors.goldGrey};
  text-decoration: none;
  font-size: 0.9rem;
  line-height: 1.7;
  transition: color 0.3s ease;

  &:hover {
    color: ${colors.mainOrange};
  }
`;

const SocialRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  justify-content: flex-end;

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const SocialLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  color: ${colors.goldGrey};
  background: rgba(255, 255, 255, 0.08);
  transition:
    color 0.25s ease,
    background 0.25s ease,
    transform 0.2s ease;

  &:hover {
    color: ${colors.mainOrange};
    background: rgba(255, 255, 255, 0.14);
    transform: translateY(-2px);
  }
`;

const FooterBottom = styled.div`
  text-align: center;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const Copyright = styled.p`
  margin: 0;
  color: ${colors.goldGrey};
  font-size: 0.85rem;
  line-height: 1.5;
`;

const CopyrightLink = styled.a`
  color: ${colors.goldGrey};
  text-decoration: underline;
  text-underline-offset: 2px;
  transition: color 0.2s ease;

  &:hover {
    color: ${colors.mainOrange};
  }
`;

/** Дозволені тільки http(s) або відносні шляхи з /. */
function isSafeCopyrightHref(href: string): boolean {
  const h = href.trim();
  if (!h) return false;
  if (h.startsWith('//')) return false;
  if (h.startsWith('/')) return true;
  return /^https?:\/\//i.test(h);
}

/**
 * Розбирає copyright з Strapi: [лейбл](url) як у Markdown.
 * Спочатку застосуй заміну {year} у батьківському рядку.
 */
function renderCopyrightWithLinks(text: string): React.ReactNode {
  const re = /\[([^\]]+)\]\(([^)]+)\)/g;
  const parts: React.ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) {
      parts.push(text.slice(last, m.index));
    }
    const label = m[1];
    const href = m[2].trim();
    if (isSafeCopyrightHref(href)) {
      const external = /^https?:\/\//i.test(href);
      parts.push(
        <CopyrightLink
          key={`cr-${i++}`}
          href={href}
          {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        >
          {label}
        </CopyrightLink>
      );
    } else {
      parts.push(m[0]);
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) {
    parts.push(text.slice(last));
  }
  return parts.length > 0 ? parts : text;
}

interface SocialLinkItem {
  id?: number;
  url?: string;
  icon?: string;
}

function normalizeSocialLinks(raw: unknown): SocialLinkItem[] {
  if (!raw) return [];
  const list = Array.isArray(raw) ? raw : [];
  return list
    .map((item: any) => ({
      id: item?.id,
      url: typeof item?.url === 'string' ? item.url.trim() : '',
      icon: typeof item?.icon === 'string' ? item.icon : 'facebook',
    }))
    .filter((item) => item.url);
}

const Footer: React.FC = () => {
  const [footerSettings, setFooterSettings] = useState<any>(null);
  const [socialLinks, setSocialLinks] = useState<SocialLinkItem[]>([]);

  useEffect(() => {
    const fetchFooterSettings = async () => {
      try {
        const raw = await apiService.getFooterSettings();
        if (raw) {
          const settings =
            raw.attributes && typeof raw.attributes === 'object'
              ? { ...raw.attributes, id: raw.id, documentId: raw.documentId }
              : raw;
          setFooterSettings(settings);
          setSocialLinks(normalizeSocialLinks(settings.socialLinks));
        }
      } catch {
        // ignore
      }
    };

    fetchFooterSettings();
  }, []);

  const copyrightRaw = footerSettings?.copyrightText ?? footerSettings?.copyright;
  const copyrightResolved =
    typeof copyrightRaw === 'string'
      ? copyrightRaw.replace(/\{year\}/gi, String(new Date().getFullYear()))
      : null;

  const defaultCopyright = `© ${new Date().getFullYear()} TransCaspian News. Всі права захищені.`;
  const copyrightContent = copyrightResolved
    ? renderCopyrightWithLinks(copyrightResolved)
    : defaultCopyright;

  return (
    <FooterContainer>
      <FooterContent>
        <FooterTop>
          <FooterSection $align="start">
            <FooterTitle>{footerSettings?.title || 'Про нас'}</FooterTitle>
            <FooterText>{footerSettings?.description || ''}</FooterText>
          </FooterSection>

          <FooterSection $align="center">
            <FooterTitle>Контакти</FooterTitle>
            <FooterText as="div">
              {footerSettings?.contactEmail && (
                <ContactLine href={`mailto:${footerSettings.contactEmail}`}>
                  {footerSettings.contactEmail}
                </ContactLine>
              )}
              {footerSettings?.contactEmail && footerSettings?.contactPhone && <br />}
              {footerSettings?.contactPhone && (
                <ContactLine href={`tel:${String(footerSettings.contactPhone).replace(/\s/g, '')}`}>
                  {footerSettings.contactPhone}
                </ContactLine>
              )}
              {!footerSettings?.contactEmail && !footerSettings?.contactPhone && <>&nbsp;</>}
            </FooterText>
          </FooterSection>

          <FooterSection $align="end">
            <FooterTitle>Соцмережі</FooterTitle>
            {socialLinks.length > 0 ? (
              <SocialRow>
                {socialLinks.map((item) => {
                  const label =
                    SOCIAL_ICON_LABELS[item.icon as SocialIconKey] || item.icon || 'Соцмережа';
                  return (
                    <SocialLink
                      key={item.id ?? item.url}
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={label}
                      aria-label={label}
                    >
                      <SocialNetworkIcon icon={item.icon || 'facebook'} size={20} />
                    </SocialLink>
                  );
                })}
              </SocialRow>
            ) : (
              <FooterText style={{ maxWidth: '14rem' }}>
                Додайте посилання в адмінці: Footer Settings → Social links.
              </FooterText>
            )}
          </FooterSection>
        </FooterTop>

        <FooterBottom>
          <Copyright>{copyrightContent}</Copyright>
        </FooterBottom>
      </FooterContent>
    </FooterContainer>
  );
};

export default Footer;
